// src/components/organisms/Modals/EditCompanyModal/index.tsx
import styles from "../AddCompanyModal/styles.module.css";
import { closeModal } from "src/store/slices/modalSlice";
import { useSelector } from "react-redux";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import closeIcon from "../../../../assets/icons/closeGray.svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Select } from "src/components/atoms/Select";
import { Input } from "src/components/atoms/Input";
import { useCallback, useState, useEffect } from "react";
import { apiClient } from "src/api/client";
import { fetchClientDetail } from "src/store/slices/clientDetailSlice";
import { useAppDispatch } from "src/store/hooks";
import useStates from "src/hooks/useStates";
import useIndustries from "src/hooks/useIndustries";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

type Option = {
  id: string | number;
  label: string;
};

const validationSchema = Yup.object({
  companyName: Yup.string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters"),
  companyForm: Yup.object().required("Company form is required"),
  state: Yup.object().required("State is required"),
  industry: Yup.object().required("Industry is required"),
});

export const EditCompanyModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { states: statesFromHook, loading: loadingStates, error: statesError } = useStates();
  const { industries: industriesFromHook, loading: loadingIndustries, error: industriesError } =
    useIndustries();

  const modalData = useSelector(
    (state: any) =>
      (state.modal.data["editCompanyModal"] as {
        companyId: number;
        clientId: number;
        companyName: string;
        typeId: number;
        stateId: number;
        industryId: number;
      }) || {}
  );

  const [companyTypes, setCompanyTypes] = useState<Option[]>([]);
  const [loadingCompanyTypes, setLoadingCompanyTypes] = useState(true);
  const [companyTypesError, setCompanyTypesError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const statesOptions = statesFromHook.map((state) => ({
    id: state.id,
    label: state.name,
  }));

  const industriesOptions = industriesFromHook.map((industry) => ({
    id: industry.id,
    label: industry.name,
  }));

  useEffect(() => {
    const fetchCompanyTypes = async () => {
      try {
        setLoadingCompanyTypes(true);
        const typesData = await apiClient.getCompaniesTypes();
        const typesOptions = typesData.map((type: any) => ({
          id: type.id,
          label: type.name || type.key,
        }));
        setCompanyTypes(typesOptions);
      } catch (error) {
        setCompanyTypesError(getErrorMessage(error));
      } finally {
        setLoadingCompanyTypes(false);
      }
    };

    fetchCompanyTypes();
  }, []);

  const isLoading = loadingCompanyTypes || loadingStates || loadingIndustries;
  const loadError = companyTypesError || statesError || industriesError;

  // Находим начальные опции по ID после загрузки
  const initialCompanyForm =
    !loadingCompanyTypes && modalData.typeId
      ? companyTypes.find((t) => t.id === modalData.typeId) ?? null
      : null;

  const initialState =
    !loadingStates && modalData.stateId
      ? statesOptions.find((s) => s.id === modalData.stateId) ?? null
      : null;

  const initialIndustry =
    !loadingIndustries && modalData.industryId
      ? industriesOptions.find((i) => i.id === modalData.industryId) ?? null
      : null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: modalData.companyName || "",
      companyForm: initialCompanyForm,
      state: initialState,
      industry: initialIndustry,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        await apiClient.editCompany(modalData.companyId, {
          name: values.companyName.trim(),
          type_id: Number(values.companyForm?.id) || 0,
          industry_id: Number(values.industry?.id) || 0,
          state_id: Number(values.state?.id) || 0,
        });

        if (modalData.clientId) {
          dispatch(fetchClientDetail(modalData.clientId));
        }

        window.dispatchEvent(new CustomEvent("companyCreated"));
        dispatch(closeModal({ type: "editCompanyModal" }));
      } catch (error: any) {
        setSubmitError(getErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const companyFormHandler = useCallback(
    (id: string | number | null) => {
      const selectedOption = companyTypes.find((item) => item.id === id);
      if (selectedOption) {
        formik.setFieldValue("companyForm", selectedOption);
      }
    },
    [companyTypes, formik]
  );

  const stateHandler = useCallback(
    (id: string | number | null) => {
      const selectedOption = statesOptions.find((item) => item.id === id);
      if (selectedOption) {
        formik.setFieldValue("state", selectedOption);
      }
    },
    [statesOptions, formik]
  );

  const industryHandler = useCallback(
    (id: string | number | null) => {
      const selectedOption = industriesOptions.find((item) => item.id === id);
      if (selectedOption) {
        formik.setFieldValue("industry", selectedOption);
      }
    },
    [industriesOptions, formik]
  );

  if (isLoading) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3>Edit company</h3>
            <div>
              <Button
                text=""
                icon={closeIcon}
                btnType={BUTTONTYPE.WITHOUT_BORDER}
                onClick={() => dispatch(closeModal({ type: "editCompanyModal" }))}
              />
            </div>
          </div>
          <div className={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3>Edit company</h3>
            <div>
              <Button
                text=""
                icon={closeIcon}
                btnType={BUTTONTYPE.WITHOUT_BORDER}
                onClick={() => dispatch(closeModal({ type: "editCompanyModal" }))}
              />
            </div>
          </div>
          <ErrorBlock error={loadError} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Edit company</h3>
          <div>
            <Button
              text=""
              icon={closeIcon}
              btnType={BUTTONTYPE.WITHOUT_BORDER}
              onClick={() => dispatch(closeModal({ type: "editCompanyModal" }))}
            />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit} className={styles.form}>
          <Input
            value={formik.values.companyName}
            label="Company name"
            onChange={formik.handleChange}
            name="companyName"
            error={formik.touched.companyName && formik.errors.companyName}
          />
          <Select
            label="Company forms"
            items={companyTypes}
            onSelect={companyFormHandler}
            selectedId={formik.values.companyForm?.id}
            className={styles.relative}
          />
          <Select
            label="State"
            items={statesOptions}
            onSelect={stateHandler}
            selectedId={formik.values.state?.id}
            className={styles.relative}
            searchable
          />
          <Select
            label="Industry"
            items={industriesOptions}
            onSelect={industryHandler}
            selectedId={formik.values.industry?.id}
            className={styles.relative}
            disabled={true}
          />
          <ErrorBlock error={submitError} />
          <Button
            text={isSubmitting ? "Saving..." : "Save changes"}
            type="submit"
            disabled={!formik.isValid || isSubmitting}
          />
        </form>
      </div>
    </div>
  );
};
